import { useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../context/LanguageContext";

const WHATSAPP_NUMBER = "218915224966";
const PHONE_NUMBER = "0915224966";

const ContactPage = () => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    const message = encodeURIComponent(
      `الاسم: ${formData.name}\nالبريد: ${formData.email}\nالهاتف: ${formData.phone}\nالموضوع: ${formData.subject}\n\nالرسالة:\n${formData.message}`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
    
    toast.success(t("messageSent"));
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: t("phone"),
      details: [PHONE_NUMBER],
      action: `tel:${PHONE_NUMBER}`,
    },
    {
      icon: Mail,
      title: t("email"),
      details: ["info@bahralrimal.ly"],
      action: "mailto:info@bahralrimal.ly",
    },
    {
      icon: MapPin,
      title: t("address"),
      details: [t("tripoliLibya")],
      action: "https://maps.google.com/?q=Tripoli,Libya",
    },
    {
      icon: Clock,
      title: t("workingHours"),
      details: [t("satToThu"), t("workTime")],
    },
  ];

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0f172a] overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-[#d4af37] to-[#38bdf8]" />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <Badge className="mb-6 bg-[#d4af37] text-[#0f172a]">{t("contactUs")}</Badge>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
              {t("getInTouch")}
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              {t("contactDescription")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24" data-testid="contact-content">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold mb-6">{t("contactInformation")}</h2>
                <p className="text-muted-foreground">
                  {t("reachOutToUs")}
                </p>
              </div>
              
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
                      <CardContent className="p-6 flex items-start gap-4">
                        <div className="p-3 bg-[#1a365d]/10 rounded-lg">
                          <info.icon className="w-6 h-6 text-[#1a365d]" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">{info.title}</h3>
                          {info.details.map((detail, i) => (
                            <p key={i} className="text-muted-foreground text-sm">{detail}</p>
                          ))}
                          {info.action && (
                            <a href={info.action} className="text-[#1a365d] text-sm hover:underline mt-2 inline-block">
                              {t("clickToContact")}
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* WhatsApp Button */}
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 p-4 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
              >
                <MessageCircle className="w-6 h-6" />
                <span className="font-semibold">{t("chatOnWhatsApp")}</span>
              </a>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="font-heading text-2xl font-bold mb-6">{t("sendMessage")}</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="mb-2 block">{t("yourName")} *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder={t("enterYourName")}
                          required
                          className="h-12"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="mb-2 block">{t("email")} *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder={t("enterYourEmail")}
                          required
                          className="h-12"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="mb-2 block">{t("phone")}</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder={t("enterYourPhone")}
                          className="h-12"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="mb-2 block">{t("subject")} *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder={t("enterSubject")}
                          required
                          className="h-12"
                          data-testid="contact-subject"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="message" className="mb-2 block">{t("message")} *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t("typeYourMessage")}
                        required
                        rows={6}
                        data-testid="contact-message"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full md:w-auto bg-[#1a365d] hover:bg-[#1a365d]/90 h-12 px-8"
                      data-testid="contact-submit"
                    >
                      {isSubmitting ? (
                        t("sending")
                      ) : (
                        <>
                          <Send className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          {t("sendMessage")}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-slate-50" data-testid="map-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-4">{t("findUsOnMap")}</h2>
            <p className="text-muted-foreground">{t("visitOurOffice")}</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d217867.99453091456!2d13.018252!3d32.8866836!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x13a892d98ece010d%3A0x5b10483cbb31b147!2sTripoli%2C%20Libya!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Bahr Al Rimal Location"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
